import axios, { AxiosInstance, AxiosResponse } from 'axios';

/**
 * EboxPermit Public "Confirmation Status" API.
 *
 * This endpoint is NOT part of the Django `/api/v1` backend — it is served by
 * the EBox web app, requires no auth, and returns **PascalCase** JSON. Because
 * of that it must NOT go through the shared `api` / `apiPublic` Axios
 * instances: their interceptors add a Bearer token and convert keys to
 * camelCase, which would mangle this response. We use a dedicated bare Axios
 * instance with no interceptors.
 *
 * Base URL: a same-origin relative path (no `/api` prefix) by default. The
 * EBox API lives on a SEPARATE host (`*.eboxpermit.com`) that sends no CORS
 * headers, so the browser must NOT call it directly — a cross-origin request
 * comes back 200 but the browser blocks JS from reading the body. Instead the
 * request stays same-origin and is forwarded to the EBox host server-side:
 *   - Local dev: `dev-server.js` forwards `/public/confirmationstatus` to DEV
 *     (`https://dev.eboxpermit.com`).
 *   - Deployed (Dev / UAT / Prod): the frontend's `nginx.conf` proxies
 *     `/public/confirmationstatus` to the per-environment EBox host (resolved
 *     from the request `Host` via an nginx `map`, defaulting to DEV).
 * Either way the browser only ever talks to its own origin, so there is no CORS.
 *
 * Override per environment via env vars (Parcel inlines `process.env.*`):
 *   - `EBOX_PUBLIC_API_HOST`   e.g. "https://uat.eboxpermit.com"
 *   - `EBOX_PUBLIC_API_PREFIX` e.g. "/api" or ""
 */

const { EBOX_PUBLIC_API_HOST, EBOX_PUBLIC_API_PREFIX } = process.env;

const host = EBOX_PUBLIC_API_HOST ?? '';

const prefix = EBOX_PUBLIC_API_PREFIX ?? '';

export const eboxPublicBaseUrl = `${host}${prefix}`;

const eboxPublicApi: AxiosInstance = axios.create({
  baseURL: eboxPublicBaseUrl,
});

export type ConfirmationStatusValue = 'Available' | 'Not Available';

/** Address block — only present when Status="Not Available" AND Live=true. */
export interface EboxAddress {
  BusinessNameOrFullName: string | null;
  FullAddress: string | null;
  Street: string;
  Street2: string | null;
  City: string;
  ZipCode: string;
  State: string;
  County: string | null;
  Country: string | null;
}

/** Raw PascalCase response from `/public/confirmationstatus`. */
export interface EboxConfirmationStatus {
  ConfirmationNumber: string;
  Status: ConfirmationStatusValue;
  /** Empty ("") for external numbers or when no EBox exists yet. */
  PublicUrl: string;
  /** true when an EBox exists for this number and is active. */
  Live: boolean;
  /** Only when Status="Not Available" AND Live=true. */
  ProjectName?: string | null;
  /** Only when Status="Not Available" AND Live=true. */
  Address?: EboxAddress | null;
}

/**
 * Look up the availability of an EBox confirmation number.
 *
 * The lookup is case-insensitive and trims surrounding whitespace server-side;
 * we trim here too so an empty/whitespace-only input never hits the network.
 *
 * Resolves with the PascalCase body on `200 OK`. Rejects on:
 *   - empty input (before any request)
 *   - `400` missing/empty `confirmationNumber`
 *   - `404` unknown confirmation number — treat as "not found", not a failure
 */
export function getConfirmationStatus(confirmationNumber: string): Promise<AxiosResponse<EboxConfirmationStatus>> {
  const trimmed = (confirmationNumber ?? '').trim();

  if (!trimmed) {
    return Promise.reject(new Error('confirmationNumber is required'));
  }

  return eboxPublicApi.get<EboxConfirmationStatus>('/public/confirmationstatus', { params: { confirmationNumber: trimmed } });
}

export default { getConfirmationStatus };
